/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { BaseBlockSnapshot, BlockType, LinkBlockSnapshot, LinkEmbedType } from "@vienna/common";

import { BaseBlock } from "src/classes/block/BaseBlock";
import { PlateSocket } from "src/classes/plate/PlateSocket/PlateSocket";
import { StringUtils } from "src/classes/utilities/StringUtils";

import { EmbedIdentifier } from "./EmbedIdentifier";
export class LinkBlock extends BaseBlock<LinkBlockSnapshot> {
  constructor(snapshot: LinkBlockSnapshot, plateSocket: PlateSocket | null) {
    super(snapshot, plateSocket);
  }

  /* -------------------------------------------------- */
  /*                Embed Type Getters                  */
  /* -------------------------------------------------- */
  public get isTweet(): boolean {
    return this.snapshot.linkEmbedType === LinkEmbedType.Twitter;
  }

  public get isYoutube(): boolean {
    return this.snapshot.linkEmbedType === LinkEmbedType.Youtube;
  }

  public get isSpotify(): boolean {
    return this.snapshot.linkEmbedType === LinkEmbedType.Spotify;
  }

  public get isInstagram(): boolean {
    return this.snapshot.linkEmbedType === LinkEmbedType.Instagram;
  }

  /* -------------------------------------------------- */
  /*                     Embed Info                     */
  /* -------------------------------------------------- */
  public get getTweetId(): string | null {
    if (!this.isTweet) return null;
    return EmbedIdentifier.getTweetIdFromURL(new URL(this.snapshot.url));
  }

  public get getYoutubeVideoId(): string | null {
    if (!this.isYoutube) return null;
    return EmbedIdentifier.getYoutubeVideoIdFromURL(new URL(this.snapshot.url));
  }

  public get getSpotifyEmbedId(): string | null {
    if (!this.isSpotify) return null;
    return EmbedIdentifier.getSpotifyEmbedIdFromURL(new URL(this.snapshot.url));
  }

  public get getInstagramEmbedId(): string | null {
    if (!this.isInstagram) return null;
    return EmbedIdentifier.getInstagramEmbedIdFromURL(new URL(this.snapshot.url));
  }

  /* -------------------------------------------------- */
  /*                  Static Methods                    */
  /* -------------------------------------------------- */
  public static shouldCreateEmbedFromPastedText(rawPasteValue: string): boolean {
    const pasteWithoutWhitespace = StringUtils.removeWhitespace(rawPasteValue);
    if (!StringUtils.isURL(pasteWithoutWhitespace)) return false;
    const url = new URL(pasteWithoutWhitespace);
    return EmbedIdentifier.getTypeFromURL(url) !== LinkEmbedType.Other;
  }

  public static newSnapshot(baseSnapshot: BaseBlockSnapshot, initialPlaintext: string): LinkBlockSnapshot {
    const initialURL = StringUtils.removeWhitespace(initialPlaintext); // remove whitespace from the plaintext
    if (!StringUtils.isURL(initialURL)) throw new Error("Trying to create link block with an invalid URL");
    const url = new URL(initialURL);
    const snapshot: LinkBlockSnapshot = {
      ...baseSnapshot,
      type: BlockType.Link,
      url: url.toString(),
      linkEmbedType: EmbedIdentifier.getTypeFromURL(url),
    };
    return snapshot;
  }
}
